import React, { useState, useEffect } from "react";
import { Box, Button, Typography, CircularProgress, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { startDailySession, endDailySession, checkActiveDailySession, getActiveDailySessionData } from "../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";
import DailySesionReportDialog from "../DesktopNavbarComponets/Session/DailySesionReportDialog";

const DailySessions = () => {
  const [loading, setLoading] = useState(false);
  const [sessionActive, setSessionActive] = useState(false); // Updated to fetch from API
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [activeSessionData, setActiveSessionData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    const fetchSessionStatus = async () => {
      setLoading(true);
      try {
        const status = await checkActiveDailySession();
        setSessionActive(status === "active");
      } catch (error) {
        console.error("❌ فشل في جلب حالة اليومية:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionStatus();
  }, []);

  const handleStartSession = async () => {
    setLoading(true);
    try {
      await startDailySession();
      alert("تم تفعيل اليومية بنجاح.");
      setSessionActive(true);
    } catch (error) {
      alert("فشل في تفعيل اليومية.");
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    setLoading(true);
    try {
      await endDailySession();
      alert("تم إيقاف اليومية بنجاح.");
      setSessionActive(false);

      // انتظار 5 ثواني ثم إعادة التشغيل تلقائياً
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // محاولة إعادة التفعيل بعد الانتظار
      try {
        await startDailySession();
        alert("تم بدا يومية جديدة بنجاح.");
        setSessionActive(true);
      } catch (startErr) {
        console.error("❌ فشل في إعادة تفعيل اليومية بعد الإيقاف:", startErr);
        alert("فشل في إعادة تفعيل اليومية بعد الإيقاف.");
      }
    } catch (error) {
      alert("فشل في إيقاف اليومية.");
    } finally {
      setLoading(false);
    }
  };

  // جلب بيانات اليومية الفعّالة وفتح مودال العرض/الطباعة
  const handleOpenReportDialog = async () => {
    setReportLoading(true);
    try {
      const branchId = localStorage.getItem("branch_id");
      if (!branchId) {
        alert("⚠️ لا يوجد branch_id في localStorage");
        setReportLoading(false);
        return;
      }
      const resp = await getActiveDailySessionData(branchId);
      const data = resp?.data || resp || null;
      setActiveSessionData(data);
      setReportDialogOpen(true);
    } catch (err) {
      console.error("❌ فشل في جلب بيانات اليومية للعرض:", err);
      alert("فشل في جلب بيانات اليومية.");
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: isDark ? "#1c1c2b" : "#f5f5f5",
        color: isDark ? "#fff" : "#232334",
      }}
    >
      <Typography variant="h6" fontWeight={700} sx={{ color: isDark ? "#f48fb1" : "#1976d2" }}>
        إدارة اليومية
      </Typography>
      {loading ? (
        <CircularProgress color="inherit" />
      ) : sessionActive ? (
        <>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenReportDialog}
            disabled={reportLoading}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              mr: 1,
              color: isDark ? "#fff" : undefined,
              borderColor: isDark ? "rgba(255,255,255,0.12)" : undefined,
            }}
          >
            {reportLoading ? "جاري التحميل..." : "عرض وطباعة اليومية قبل التقفيل"}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleEndSession}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              bgcolor: isDark ? "#d32f2f" : "#f44336",
              "&:hover": { bgcolor: isDark ? "#b71c1c" : "#e53935" },
            }}
          >
            إيقاف اليومية
          </Button>
        </>
      ) : (
        <Button
          variant="contained"
          color="success"
          onClick={handleStartSession}
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            bgcolor: isDark ? "#388e3c" : "#4caf50",
            "&:hover": { bgcolor: isDark ? "#2e7d32" : "#43a047" },
          }}
        >
          تفعيل اليومية
        </Button>
      )}
      {/* مودال عرض وطباعة اليومية مُغلف بـ MUI Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "98vw", sm: "98vw", md: "1800px", lg: "2000px", xl: "2200px" }, // عرض أكبر جداً للشاشات الكبيرة
            height: { xs: "90vh", sm: "93vh", md: "95vh", lg: "96vh", xl: "97vh" },     // ارتفاع أكبر جداً
            maxHeight: "99vh",
            borderRadius: 1,
            m: 0,
          },
        }}
        BackdropProps={{ sx: { backdropFilter: "blur(2px)" } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography component="span">عرض وطباعة اليومية</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setReportDialogOpen(false)}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ height: "calc(100% - 112px)", overflowY: "auto", p: 1 }}>
          {/* نعرض مكوّن التقرير داخل محتوى المودال و نمرّر بيانات اليومية */}
          <DailySesionReportDialog sessionData={activeSessionData} onClose={() => setReportDialogOpen(false)} />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setReportDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailySessions;

